<template>
  <div class="user-info-form">
    <div class="form-header">
      <h1 class="title">睡眠问诊小助手</h1>
      <p class="subtitle">请填写以下信息开始问卷</p>
    </div>

    <van-form @submit="handleSubmit" class="form-content">
      <van-cell-group>
        <!-- 姓名输入 -->
        <van-field v-model="formData.name" name="name" center label="姓名" placeholder="请输入您的姓名" :rules="nameRules"
          required clearable show-word-limit label-width="90px" />

        <!-- 手机号输入 -->
        <van-field v-model="formData.tel_phone" name="tel_phone" center label="手机号" placeholder="请输入手机号"
          :rules="phoneRules" required clearable maxlength="11" type="tel" label-width="90px" />

        <!-- 证件类型选择 -->
        <van-field :model-value="selectedIdTypeLabel" name="idType" center label="证件类型" placeholder="请选择证件类型" readonly
          is-link required @click="showIdTypePicker = true" label-width="90px" />

        <!-- 证件号码输入 -->
        <van-field v-model="formData.idNumber" name="idNumber" center label="证件号码" placeholder="请输入证件号码"
          :rules="idNumberRules" required clearable maxlength="30" label-width="90px" />

        <!-- 出生日期 -->
        <van-field :model-value="formData.birthday" name="birthday" center label="出生日期" placeholder="请选择出生日期"
          readonly is-link required @click="showBirthdayPicker = true" label-width="90px" />

        <!-- 年龄 -->
        <van-field v-model="formData.age" name="age" center label="年龄" placeholder="请输入年龄" type="digit"
          :rules="ageRules" required clearable maxlength="3" label-width="90px">
          <template #extra>
            <span class="age-unit">岁</span>
          </template>
        </van-field>

        <!-- 性别 -->
        <van-field name="sex" center label="性别" label-width="90px" readonly required>
          <template #input>
            <van-radio-group v-model="formData.sex" direction="horizontal">
              <van-radio name="man">男</van-radio>
              <van-radio name="woman">女</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <!-- 门诊ID -->
        <van-field v-model="formData.his_id" name="his_id" center label="门诊ID" placeholder="请输入门诊ID"
          :rules="hisIdRules" required clearable label-width="90px" />

        <!-- 是否填写问卷（隐藏，默认为true） -->
      </van-cell-group>

      <!-- 提交按钮 -->
      <div class="submit-section">
        <van-button type="primary" size="large" :disabled="!isFormValid" :loading="isSubmitting" loading-text="正在提交..."
          native-type="submit" block @click="handleSubmit">
          开始问卷
        </van-button>
      </div>
    </van-form>

    <!-- 证件类型选择器 -->
    <van-popup v-model:show="showIdTypePicker" position="bottom">
      <van-picker :columns="idTypeOptions" @confirm="onIdTypeConfirm" @cancel="showIdTypePicker = false" />
    </van-popup>

    <!-- 出生日期选择器 -->
    <van-popup v-model:show="showBirthdayPicker" position="bottom">
      <van-date-picker v-model="birthdayPickerValue" title="选择出生日期" :min-date="minBirthDate" :max-date="maxBirthDate"
        @confirm="onBirthdayConfirm" @cancel="showBirthdayPicker = false" />
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from "vue";
import { showToast } from "vant";
import { useRouter } from "vue-router";
import { useAppStore } from "@/stores/app.js";
import { useChatStore } from "@/stores/chat.js";
import { unlockAudio } from "@/composables/useAudio.js";

const router = useRouter();
const appStore = useAppStore();
const chatStore = useChatStore();


// 状态
const formData = reactive({
  name: "",
  tel_phone: "",
  idType: "",
  idNumber: "",
  birthday: "",
  age: "",
  sex: "",
  his_id: "",
  need_questionnaire: true, // 是否填写问卷，默认为true
});

const isSubmitting = ref(false);
const showIdTypePicker = ref(false);
const showBirthdayPicker = ref(false);

// 出生日期选择器的值（数组格式: [year, month, day]）
const birthdayPickerValue = ref(['1990', '01', '01']);

// 出生日期范围
const minBirthDate = new Date(1900, 0, 1);
const maxBirthDate = new Date();

// 证件类型选项
const idTypeOptions = [
  { text: "身份证", value: "id_card" },
  { text: "军官证", value: "official_card" },
  { text: "文职证", value: "civilian_card" },
];

// 验证规则
const nameRules = [
  { required: true, message: "请输入姓名" },
  { pattern: /^[\u4e00-\u9fa5a-zA-Z\s]{1,20}$/, message: "姓名格式不正确" },
];

const phoneRules = [
  { required: true, message: "请输入手机号" },
  { pattern: /^1[3-9]\d{9}$/, message: "手机号格式不正确" },
];

const idNumberRules = [
  { required: true, message: "请输入证件号码" },
  {
    validator: (value) => {
      if (!value) return false;
      if (formData.idType === "id_card") {
        // 身份证号码验证（简化版）
        return /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/.test(
          value
        );
      }
      // 其他证件类型的基本验证
      return value.length >= 6 && value.length <= 30;
    },
    message: "证件号码格式不正确",
  },
];

const ageRules = [
  { required: true, message: "请输入年龄" },
  {
    validator: (value) => {
      const age = parseInt(value, 10);
      return !isNaN(age) && age >= 0 && age <= 150;
    },
    message: "请输入有效年龄(0-150)",
  },
];

const hisIdRules = [
  { required: true, message: "请输入门诊ID" },
];

// 计算属性
const isFormValid = computed(() => {
  return (
    formData.name.trim() &&
    formData.tel_phone.trim() &&
    formData.idType &&
    formData.idNumber.trim() &&
    formData.birthday &&
    formData.age &&
    formData.sex &&
    formData.his_id.trim() &&
    nameRules.every(
      (rule) => !rule.pattern || rule.pattern.test(formData.name)
    ) &&
    phoneRules.every(
      (rule) => !rule.pattern || rule.pattern.test(formData.tel_phone)
    ) &&
    idNumberRules.every(
      (rule) => !rule.validator || rule.validator(formData.idNumber)
    ) &&
    ageRules.every(
      (rule) => !rule.validator || rule.validator(formData.age)
    )
  );
});

// 获取当前选中的证件类型标签
const selectedIdTypeLabel = computed(() => {
  const option = idTypeOptions.find((opt) => opt.value === formData.idType);
  return option?.text || "";
});

// 监听证件类型变化，清空证件号码
const onIdTypeConfirm = ({ selectedOptions }) => {
  const selected = selectedOptions[0];
  formData.idType = selected.value;
  formData.idNumber = ""; // 切换证件类型时清空号码
  showIdTypePicker.value = false;
};

/**
 * 根据出生日期计算年龄
 * @param {string} birthday - 出生日期 YYYY-MM-DD
 * @returns {number} 年龄
 */
const calculateAge = (birthday) => {
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * 出生日期选择确认
 * @param {{ selectedValues: string[] }} param0 - 选中的值
 */
const onBirthdayConfirm = ({ selectedValues }) => {
  // selectedValues: ['2000', '01', '15']
  const birthday = selectedValues.join('-');
  formData.birthday = birthday;
  // 自动计算年龄
  formData.age = String(calculateAge(birthday));
  showBirthdayPicker.value = false;
};

// 提交表单
const handleSubmit = async () => {
  // 立即尝试解锁音频（需要在用户交互的同步栈中执行）
  unlockAudio();

  if (!isFormValid.value) {
    showToast({ message: "请完整填写所有信息" });
    return;
  }

  try {
    isSubmitting.value = true;

    // 标记用户已交互
    appStore.userHasInteracted = true;

    // 保存用户信息到应用状态
    appStore.setUserInfo({ ...formData });

    // 显示成功提示
    showToast({
      type: "success",
      message: "信息提交成功",
      duration: 1500
    });

    // 延迟一下再跳转，让用户看到成功提示
    setTimeout(() => {
      // 跳转到聊天页面
      appStore.setCurrentStep('chat');
      // 实际进行路由跳转
      router.push('/chat');
    }, 1500);

  } catch (error) {
    console.error("提交表单失败:", error);
    showToast({ type: "fail", message: "提交失败，请重试" });
  } finally {
    isSubmitting.value = false;
  }
};


// 重置表单
const resetForm = () => {
  formData.name = "";
  formData.tel_phone = "";
  formData.idType = "";
  formData.idNumber = "";
  formData.birthday = "";
  formData.age = "";
  formData.sex = "";
  formData.his_id = "";
  formData.need_questionnaire = true;
  birthdayPickerValue.value = ['1990', '01', '01'];
};

// 暴露方法给父组件
defineExpose({
  resetForm,
});
</script>

<style lang="scss" scoped>
.user-info-form {
  min-height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 15px;
  padding-bottom: calc(30px + env(safe-area-inset-bottom, 0px)); // 适配底部安全区
  box-sizing: border-box;
}

.form-header {
  text-align: center;
  margin-bottom: 16px;
  padding-top: 20px;

  .title {
    font-size: 24px;
    font-weight: bold;
    color: white;
    margin-bottom: 6px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .subtitle {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
}

.form-content {
  background: white;
  border-radius: 16px;
  padding: 20px 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

// 表单项紧凑样式
:deep(.van-cell) {
  padding: 12px 16px;
}

:deep(.van-field__label) {
  font-size: 14px;
}

.age-unit {
  color: #969799;
  font-size: 14px;
  margin-left: 4px;
}

.submit-section {
  margin-top: 24px;
  padding-bottom: 10px;
}

// 提交按钮样式
:deep(.van-button) {
  height: 46px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 23px;

  &.van-button--primary:not(:disabled) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
  }
}

// 开发环境跳过按钮样式
.skip-button {
  margin-top: 15px;

  :deep(.van-button) {
    background: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%);
    color: white;
    border: none;
    font-weight: bold;

    &:active {
      background: linear-gradient(135deg, #ff5252 0%, #ff9800 100%);
    }
  }
}

// 小屏适配
@media (max-width: 375px) {
  .user-info-form {
    padding: 12px;
  }

  .form-header {
    padding-top: 15px;
    margin-bottom: 12px;

    .title {
      font-size: 22px;
    }

    .subtitle {
      font-size: 13px;
    }
  }

  .form-content {
    padding: 16px 12px;
    border-radius: 12px;
  }

  :deep(.van-cell) {
    padding: 10px 12px;
  }

  .submit-section {
    margin-top: 20px;
  }
}
</style>